import '../styles/FavouriteTooltip.css';
import React, { useEffect, useRef, useState } from 'react';
import LoadingSpinner from '../utils/LoadingSpinner';
import { FaSearch, FaSort, FaAndroid, FaSitemap, FaStar, FaRegStar } from 'react-icons/fa';
import '../styles/Reports.css';
import '../styles/DeviceModal.css';
import './../styles/messageContainer.css';
import './../styles/RiskLevelModal.css';
import InformationUsersSection from './InformationUsersSection';
import { faTriangleExclamation, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSite } from './../context/SiteContext';
import { useAuth } from '../context/AuthContext';
import Button from './ui/Button';
import Pagination from './ui/Pagination';
import RiskLevelModal from './modals/RiskLevelModal';
import FavouriteDeviceModal from './modals/FavouriteDeviceModal';
import { useUsersTableData } from '../hooks/useUsersTableData';
import { getRiskButtonLabel } from '../utils/riskLevelUtils';
import { UserType } from '../constants/userType';
import deviceService from '../services/DeviceService';

const UsersTable = () => {
  const { selectedSiteId } = useSite();
  const { userType } = useAuth();
  const isPoliceUser = userType === UserType.POLICE;
  const canShowRiskLevel = userType !== UserType.POLICE;
  const [riskLevelModal, setRiskLevelModal] = useState({ open: false, user: null });
  const [favouriteModal, setFavouriteModal] = useState({ open: false, user: null });
  const dropdownRef = useRef(null);

  const {
    sortedUsers,
    paginatedData,
    currentPage,
    totalPages,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    canFetchUsers,
    setCanFetchUsers,
    sortConfig,
    handleSort,
    applyFilters,
    showAllUsers,
    exportToXlsx,
    handlePageChange,
    loadingList,
    errorList,
    userList,
    handleSelectUser,
    saveRiskLevel,
    saveFavouriteState,
  } = useUsersTableData(selectedSiteId, { includeRiskLevel: canShowRiskLevel });

  const openRiskLevelModal = (user) => setRiskLevelModal({ open: true, user });
  const closeRiskLevelModal = () => setRiskLevelModal({ open: false, user: null });
  const openFavouriteModal = (user) => setFavouriteModal({ open: true, user });
  const closeFavouriteModal = () => setFavouriteModal({ open: false, user: null });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setCanFetchUsers(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setCanFetchUsers]);

  const renderSortArrow = (columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    }
    return <FaSort className="reports-font-style-grey" />;
  };

  const fmtNoComma = (d) =>
    d
      ? `${new Date(d).toLocaleDateString('en-GB')} ${new Date(d).toLocaleTimeString('en-GB', { hour12: false })}`
      : '—';

  const getModelList = (models) => {
    if (Array.isArray(models)) {
      return models.map((model) => String(model).trim()).filter(Boolean);
    }

    if (models == null) return [];

    return String(models)
      .split(',')
      .map((model) => model.trim())
      .filter(Boolean);
  };

  const renderModels = (models) => {
    const modelList = getModelList(models);

    if (modelList.length === 0) return '—';

    return (
      <span className="users-table__clones-capsule" title={modelList.join(', ')}>
        {modelList.map((model) => (
          <span className="users-table__clones-segment" key={model}>
            {model}
          </span>
        ))}
      </span>
    );
  };

  const getFavouriteEntry = (user) => ({
    ...user,
    isFavourite: Boolean(user?.isFavourite ?? user?.isFavourites),
    notes: String(user?.favouriteNotes ?? user?.notes ?? '').trim(),
  });

  const saveFavourite = async (user, favouriteData) => {
    const deviceId = String(user?.visorId || '').trim();
    if (!deviceId) {
      throw new Error('A device id is required to save a favourite.');
    }

    const wasFavourite = Boolean(user?.isFavourite ?? user?.isFavourites);
    const notes = favouriteData.notes.trim();

    if (favouriteData.isFavourite && wasFavourite) {
      await deviceService.updateFavourites({
        deviceId,
        isFavourite: 1,
        favouriteNotes: notes,
      });
    } else if (favouriteData.isFavourite) {
      await deviceService.setFavourites({
        deviceId,
        isFavourite: 1,
        favouriteNotes: notes,
      });
    } else if (wasFavourite) {
      await deviceService.deleteFavourites(deviceId);
    }

    saveFavouriteState(user, {
      isFavourite: favouriteData.isFavourite,
      notes,
    });
  };

  return (
    <div>
      <div className="user-report-advanced-search">
        <div className="report-search-group">
          <div className="report-search-input-group">
            <span className="report-search-input-group-text">
              <FaSearch />
            </span>
            <div
              ref={dropdownRef}
              style={{ position: 'relative', display: 'inline-block' }}
            >
              <input
                type="text"
                placeholder="Search users"
                value={searchQuery}
                onChange={(e) => {
                  setCanFetchUsers(true);
                  setSearchQuery(e.target.value);
                }}
                className="report-search-form-control"
                autoComplete="off"
              />

              {canFetchUsers && searchQuery.length >= 3 && (
                <ul className="user-dropdown">
                  {loadingList && <li className="loading">Loading…</li>}
                  {errorList && <li className="error">Error: {errorList.message}</li>}
                  {!loadingList && !errorList && userList.length === 0 && (
                    <li className="no-results">No users found</li>
                  )}
                  {!loadingList && !errorList && userList.map((u) => (
                    <li key={u.id} onClick={() => handleSelectUser(u)}>
                      {u.username}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <Button
            onClick={() => {
              applyFilters();
            }}
          >
            Search
          </Button>

          <Button onClick={showAllUsers}>
            Show All
          </Button>

          <Button
            buttonStyle="btn-alternate"
            onClick={exportToXlsx}
            disabled={sortedUsers.length === 0}
          >
            Export XLSX
          </Button>
        </div>
      </div>

      <InformationUsersSection filePath="md/userCaptureService.md" />

      <div className="user-table-custom-container-extended">
        <div className="user-report-table-section-extended pagination-container">
          {isLoading && <LoadingSpinner />}

          {!isLoading && error ? (
            <div className="message-container">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                size="3x"
                className="message-icon"
              />
              <p className="message-text">
                {error || 'There are no images queued for review at this time.'}
              </p>
            </div>
          ) : (
            <>
              <div className="report-table-container">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('groupName')}>
                        Group {renderSortArrow('groupName')}
                      </th>
                      <th onClick={() => handleSort('visorId')}>
                        <span className="users-table__visor-header-content">
                          {isPoliceUser && (
                            <button
                              type="button"
                              className="users-table__favourite-sort"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleSort('isFavourite');
                              }}
                              title="Sort by favourite"
                              aria-label="Sort by favourite"
                            >
                              <FaStar />
                              {renderSortArrow('isFavourite')}
                            </button>
                          )}
                          <span>Visor Reference {renderSortArrow('visorId')}</span>
                        </span>
                      </th>
                      <th onClick={() => handleSort('version')}>
                        eSafe Version {renderSortArrow('version')}
                      </th>
                      <th onClick={() => handleSort('os')}>
                        OS {renderSortArrow('os')}
                      </th>
                      <th onClick={() => handleSort('models')}>
                        Clones {renderSortArrow('models')}
                      </th>
                      <th onClick={() => handleSort('lastOnline')}>
                        Last Online {renderSortArrow('lastOnline')}
                      </th>
                      <th onClick={() => handleSort('installationDate')}>
                        Installation Date {renderSortArrow('installationDate')}
                      </th>
                      <th onClick={() => handleSort('captureServiceRunning')}>
                        Device Monitoring On? {renderSortArrow('captureServiceRunning')}
                      </th>
                      <th onClick={() => handleSort('notifications')}>
                        Notifications {renderSortArrow('notifications')}
                      </th>
                      {canShowRiskLevel && (
                        <th onClick={() => handleSort('riskLevel')}>
                          Risk Level {renderSortArrow('riskLevel')}
                        </th>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.map((item, idx) => {
                      const favouriteEntry = getFavouriteEntry(item);
                      const favouriteTooltipId = `users-favourite-tooltip-${idx}`;
                      const isMonitoringOn = String(item.captureServiceRunning ?? '').trim().toLowerCase() === 'true';
                      const areNotificationsAllowed = String(item.notifications ?? '').trim().toLowerCase() === 'allowed';

                      return (
                        <React.Fragment key={idx}>
                          <tr>
                            <td><FaSitemap className="reports-font-style-grey" /> {item.groupName || '—'}</td>
                            <td>
                              <div className="users-table__visor-cell">
                                {isPoliceUser && (
                                  <button
                                    type="button"
                                    className={`users-table__favourite-button ${
                                      favouriteEntry?.isFavourite ? 'is-favourite' : ''
                                    }`}
                                    onClick={() => openFavouriteModal(item)}
                                    aria-describedby={favouriteTooltipId}
                                    aria-label={
                                      favouriteEntry?.isFavourite
                                        ? `Edit favourite for ${item.visorId}`
                                        : `Add favourite for ${item.visorId}`
                                    }
                                  >
                                    {favouriteEntry?.isFavourite ? <FaStar /> : <FaRegStar />}
                                    <span className="favourite-tooltip" id={favouriteTooltipId} role="tooltip">
                                      <span className="favourite-tooltip-title">Favourite notes</span>
                                      <span className="favourite-tooltip-body">
                                        {favouriteEntry.notes || 'No favourite notes have been recorded for this visor.'}
                                      </span>
                                    </span>
                                  </button>
                                )}
                                <span className="users-table__visor-text">
                                  <FaAndroid className="reports-font-style-grey" /> {item.visorId}
                                </span>
                              </div>
                            </td>
                            <td>{item.version || '—'}</td>
                            <td>{item.os ? `Android ${item.os}` : '—'}</td>
                            <td className="users-table__clones-cell">{renderModels(item.models)}</td>
                            <td>{fmtNoComma(item.lastOnline)}</td>
                            <td>{fmtNoComma(item.installationDate)}</td>
                            <td>
                              <span className={`monitoring-status-capsule ${isMonitoringOn ? 'is-positive' : 'is-negative'}`}>
                                {isMonitoringOn ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td>
                              <span className={`monitoring-status-capsule ${areNotificationsAllowed ? 'is-positive' : 'is-negative'}`}>
                                {areNotificationsAllowed ? 'Allowed' : 'Denied'}
                              </span>
                            </td>
                            {canShowRiskLevel && (
                              <td className="risk-level-cell">
                                <button
                                  className={`risk-level-button ${
                                    item.riskLevel?.toLowerCase() === 'high' ? 'risk-high' :
                                    item.riskLevel?.toLowerCase() === 'medium' ? 'risk-medium' :
                                    'risk-none'
                                  }`}
                                  onClick={() => openRiskLevelModal(item)}
                                  title="Edit Risk Level"
                                >
                                  {getRiskButtonLabel(item.riskLevel)}
                                  <FontAwesomeIcon icon={faPenToSquare} />
                                </button>
                              </td>
                            )}
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {sortedUsers.length > 0 && totalPages > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  totalRecords={sortedUsers.length}
                  docked
                />
              )}
            </>
          )}
        </div>

        {canShowRiskLevel && riskLevelModal.open && riskLevelModal.user && (
          <RiskLevelModal
            isOpen={riskLevelModal.open}
            nominal={riskLevelModal.user}
            onClose={closeRiskLevelModal}
            onSave={(riskData) => {
              saveRiskLevel(riskLevelModal.user, riskData);
              closeRiskLevelModal();
            }}
          />
        )}

        {isPoliceUser && favouriteModal.open && favouriteModal.user && (
          <FavouriteDeviceModal
            isOpen={favouriteModal.open}
            user={favouriteModal.user}
            initialValue={getFavouriteEntry(favouriteModal.user)}
            onClose={closeFavouriteModal}
            onSave={async (favouriteData) => {
              try {
                await saveFavourite(favouriteModal.user, favouriteData);
                closeFavouriteModal();
              } catch (error) {
                console.error('Failed to save favourite', error);
                window.alert('Failed to save favourite. Please try again.');
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default UsersTable;
